/*
 * Motail quick-add
 * ----------------
 * Collection-page add-to-cart with an inline pack picker (see snippets/motail-quick-add.liquid).
 *
 * Reuses the Flow theme's public surface so it needs no changes to the vendor bundles:
 *   - window.routes.cart_add_url / cart_url
 *   - window.eventBus.emit('update:cart:drawer', json)  -> re-renders the drawer from json.sections['cart-drawer']
 *   - window.eventBus.emit('open:cart:drawer')          -> opens the cart drawer
 *   - window.wetheme.updateCartCount(cart)              -> updates the header cart badge
 *
 * The add flow mirrors assets/component-product-card-basic.js.
 */
class MotailQuickAdd extends HTMLElement {
  constructor() {
    super();
    this.trigger = this.querySelector('[data-quick-add-trigger]');
    this.panel = this.querySelector('[data-quick-add-popover]');
    this.options = Array.from(this.querySelectorAll('[data-quick-add-variant]'));
    this.errorElement = this.querySelector('[data-quick-add-error]');
    this.cartCountIndicator = document.querySelector('[data-cart-count-indicator]');
    this.isOpen = false;
    this.isProcessing = false;

    this.toggle = this.toggle.bind(this);
    this.close = this.close.bind(this);
    this.handleOptionClick = this.handleOptionClick.bind(this);
    this.handleDocumentPointer = this.handleDocumentPointer.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    if (window.wetheme && window.wetheme.webcomponentRegistry) {
      window.wetheme.webcomponentRegistry.register({ key: 'motail-quick-add' });
    }
    if (this.trigger) this.trigger.addEventListener('click', this.toggle);
    this.options.forEach((option) => option.addEventListener('click', this.handleOptionClick));
    this.addEventListener('keydown', this.handleKeydown);
  }

  disconnectedCallback() {
    if (this.trigger) this.trigger.removeEventListener('click', this.toggle);
    this.options.forEach((option) => option.removeEventListener('click', this.handleOptionClick));
    this.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('click', this.handleDocumentPointer, true);
  }

  /* ===== Popover open / close ===== */

  toggle(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    /* ===== Only one picker open at a time ===== */
    document.querySelectorAll('motail-quick-add[data-open]').forEach((element) => {
      if (element !== this && typeof element.close === 'function') element.close();
    });

    this.isOpen = true;
    this.setAttribute('data-open', '');
    this.panel.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');

    /* ===== Defer the outside-click listener so the opening click doesn't immediately close it ===== */
    requestAnimationFrame(() => {
      document.addEventListener('click', this.handleDocumentPointer, true);
    });
  }

  close() {
    this.isOpen = false;
    this.removeAttribute('data-open');
    if (this.panel) this.panel.hidden = true;
    if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.handleDocumentPointer, true);
    this.clearError();
  }

  handleDocumentPointer(event) {
    if (!this.contains(event.target)) this.close();
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
      if (this.trigger) this.trigger.focus();
    }
  }

  /* ===== Add to cart ===== */

  async handleOptionClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget;
    if (button.disabled || this.isProcessing) return;

    const variantId = button.getAttribute('data-quick-add-variant');
    if (!variantId) return;

    this.isProcessing = true;
    this.clearError();
    this.setOptionLoading(button, true);

    try {
      const body = new FormData();
      body.append('id', variantId);
      body.append('quantity', '1');
      body.append('sections', 'cart-drawer');

      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const json = await response.json();

      /* ===== Sold out / not enough stock etc. ===== */
      if (response.status === 422 || json.status === 422) {
        this.showError(json.description || json.message);
        return;
      }

      /* ===== Re-render the cart drawer from the bundled section, update the count, open it ===== */
      if (window.eventBus) window.eventBus.emit('update:cart:drawer', json);
      this.updateCartCount();
      this.close();
      if (window.eventBus) window.eventBus.emit('open:cart:drawer');
    } catch (error) {
      console.error('Motail quick-add: unable to add to cart', error);
      this.showError();
    } finally {
      this.setOptionLoading(button, false);
      this.isProcessing = false;
    }
  }

  async updateCartCount() {
    if (!this.cartCountIndicator) return;
    if (!window.wetheme || typeof window.wetheme.updateCartCount !== 'function') return;
    try {
      const response = await fetch(`${window.routes.cart_url}?view=compare`);
      const cart = await response.json();
      window.wetheme.updateCartCount(cart);
    } catch (error) {
      /* Count refresh is non-fatal — the drawer already shows the new item. */
    }
  }

  /* ===== UI helpers ===== */

  setOptionLoading(button, loading) {
    button.classList.toggle('is-loading', loading);
    button.setAttribute('aria-busy', loading ? 'true' : 'false');
  }

  showError(message) {
    if (!this.errorElement) return;
    this.errorElement.textContent = message || 'Er ging iets mis. Probeer het opnieuw.';
    this.errorElement.hidden = false;
  }

  clearError() {
    if (!this.errorElement) return;
    this.errorElement.hidden = true;
    this.errorElement.textContent = '';
  }
}

if (!customElements.get('motail-quick-add')) {
  customElements.define('motail-quick-add', MotailQuickAdd);
}
